import { useState, useMemo, useCallback, useEffect } from "react";
import DialogContent from "@mui/material/DialogContent";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { Box, DialogActions, Grid, Typography, useTheme } from "@mui/material";
import { ModalTitle, ModalMessage, ModalButton, ModalErrorText, ModalWarningText } from "../DialogModalComponents";
import { EventModalTypes } from "../DialogModalWrapper";
// ModalWarningText
import { findMosaicNode, IMosaicNode } from "../../../utils/MosaicTree";
import { IFullPairingPosition, IPairing, IPosition } from "@vmosolutions/oc-interfaces";
// API Calls
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { ICrewMember } from "src/redux/reducers/CrewScheduleReducer";
import { PositionBadge } from "src/components/CrewSchedule/PositionBadge";
import { GhostPairingConflicts } from "src/pages/CrewSchedule/gl/CrewScheduleGL";
import { DialogModalStyles } from "../DialogModalWrapper";

interface IAssignmentProps {
  assignCrewId: string;
  assignCrewPosition: IPosition;
  pairingId: string;
  conflict: string;
}

interface ISplitPairing {
  isOpen: boolean;
  handleClose: (value: boolean) => void;
  setActiveEventModal: (EventModalTypes: EventModalTypes) => void;
  modalProps?: IAssignmentProps;
}

// Check to see if an available position slot exists on a crew member list of qualifications.
const crewHasPosition = (crewPos: IPosition[], pairingPos: IFullPairingPosition): boolean => {
  return crewPos?.length ? !!crewPos.find((pos) => pos._id === pairingPos.position._id) : false;
};

/**
 *  Modal to Assign Crew to Chosen Pairing's available position
 * This modal will display any known conflicts but allow users to go ahead with the conflicted assignment regardless
 */
export const AssignCrewToPairingModal = (props: ISplitPairing): JSX.Element => {
  const { isOpen, handleClose, setActiveEventModal, modalProps } = props;
  const dispatch = useDispatch();
  const theme = useTheme();
  const crewMembers = useSelector((state: IRootState) => state.crewSchedule.crewMembers);
  const assignToCrewMember: ICrewMember = useMemo(() => {
    return crewMembers?.length && modalProps?.assignCrewId
      ? crewMembers.find((member) => member._id === modalProps.assignCrewId)
      : null;
  }, [crewMembers, modalProps.assignCrewId]);

  const [assignPairing, setAssignPairing] = useState<IPairing>(null);
  const pairingPositions = useMemo(() => assignPairing?.positions ?? [], [assignPairing]);
  const [updatedPositions, setUpdatedPositions] = useState<IFullPairingPosition[]>([]);

  const positions = useSelector((state: IRootState) => state.structures.customerStructure.positions);

  // Desired position assignment (this can be modified by the user if that position is already occupied)
  const [assignPos, setAssignPos] = useState<IPosition>(modalProps.assignCrewPosition);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // EDIT EXISTING PAIRING
  useEffect(() => {
    if (modalProps?.pairingId) {
      const getPairingAction = async (): Promise<void> => {
        setIsSubmitting(true);
        const loadedPairing = await AmplifyAPIWrapper.Get<IPairing>(`/pairings/${modalProps?.pairingId}`, null);
        if (loadedPairing?._id) {
          setAssignPairing(loadedPairing);
        }
        setIsSubmitting(false);
      };
      getPairingAction();
    }
  }, [modalProps?.pairingId]);

  // Check to see if any editors are open containing this pairing id.
  const currentNode: IMosaicNode = useSelector((state: IRootState) => state.windowManager.currentNode);
  const openEditor = useMemo(() => {
    return findMosaicNode(currentNode, [], "PairingEditorWindow", { pairingId: modalProps?.pairingId });
  }, [currentNode, modalProps?.pairingId]);
  const openEditorError = openEditor
    ? "Selected pairing has an open editor window, please close it before assigning crew."
    : null;

  const [apiError, setApiError] = useState<string>(null);

  // console.log("assignPairing", assignPairing);
  const { assignCrewId, assignCrewPosition, pairingId, conflict } = modalProps;
  const { firstName, lastName, crewInfo } = assignToCrewMember;

  const posNoChange = assignPos?.title === assignCrewPosition?.title;
  const isPosConflict = conflict === GhostPairingConflicts.POSITION_FULFILLED;
  const noPosConflict = conflict === GhostPairingConflicts.NO_POSITION_MATCH;

  const assignCrewToPosition = useCallback(
    (position: IFullPairingPosition) => {
      const fulfillPosition: IFullPairingPosition = {
        ...position,
        crew: assignToCrewMember,
      };
      const updatedPairingPositions = pairingPositions.map((pp) => {
        if (pp.position.title === fulfillPosition.position.title) {
          return fulfillPosition;
        }
        return pp;
      });
      setUpdatedPositions(updatedPairingPositions);
      const assignPos = positions?.find((pos) => pos.title === position.position.title);
      setAssignPos(assignPos);
    },
    [pairingPositions, assignToCrewMember, positions]
  );

  const assignPairingToCrew = useCallback(async (): Promise<void> => {
    setIsSubmitting(true);
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: "Assigning Pairing",
      successMessage: "Pairing Assignment Successful",
      errorMessage: "Error: Unable to Assign Pairing",
    };
    const response: any = await AmplifyAPIWrapper.Put(
      `/assignCrewToPairing/${pairingId}/crewId/${assignCrewId}`,
      null,
      notifications
    );
    setIsSubmitting(false);
    if (response?._id) {
      handleClose(false);
      setActiveEventModal(EventModalTypes.None);
    } else if (response?.error || response?.errors) {
      setApiError(response?.error ?? response?.errors.map((err: any) => err.description).join(". "));
    }
  }, [assignCrewId, dispatch, handleClose, pairingId, setActiveEventModal]);

  const handleCancel = (): void => {
    //cancel modal
    handleClose(false);
    setActiveEventModal(EventModalTypes.None);
  };

  if (isOpen && assignPairing) {
    return (
      <DialogContent sx={DialogModalStyles(theme)}>
        <div className="modal_content" style={{ display: "flex", maxWidth: 500 }}>
          <ModalTitle title="Assign Crew To Pairing" />
          <ModalMessage
            message={
              <Box
                sx={{
                  "& .position_badge_wrapper": {
                    display: "inline-block",
                  },
                }}
              >
                {noPosConflict ? (
                  <>
                    Cannot assign {`${firstName} ${lastName}`} as
                    <Box sx={{ display: "inline !important", mx: 0.5, position: "relative", top: "-2px" }}>
                      <PositionBadge filled={true} position={assignCrewPosition} />
                    </Box>
                    {`${assignPos?.description} on ${assignPairing?.shortId}`}
                  </>
                ) : (
                  <>
                    Assign {`${firstName} ${lastName}`} as
                    <Box sx={{ display: "inline !important", mx: 0.5, position: "relative", top: "-2px" }}>
                      <PositionBadge filled={true} position={assignCrewPosition} />
                    </Box>
                    {`${assignPos?.description} on ${assignPairing?.shortId}?`}
                  </>
                )}
              </Box>
            }
          />

          {isPosConflict && (
            <Grid container style={{ width: "auto", margin: "0px 20px" }} alignItems="center">
              <Typography variant="caption" style={{ marginRight: 10 }}>
                Click to select desired position to this crew assignment
              </Typography>
              {pairingPositions.map((position, index) => {
                const selected = position.position.title === assignPos.title;
                const isQualified = crewHasPosition(crewInfo.positions, position);
                return !position?.crew?._id && isQualified ? (
                  <div
                    key={index}
                    onClick={() => assignCrewToPosition(position)}
                    style={{ border: selected ? "1px solid lightblue" : "none", padding: 1, borderRadius: 3 }}
                  >
                    <PositionBadge {...position} />
                  </div>
                ) : (
                  <div key={index} style={{ opacity: 0.5 }}>
                    <PositionBadge
                      {...position}
                      filled={position?.crew?._id}
                      isInactive={!position?.crew?.crewInfo?.isActive}
                    />
                  </div>
                );
              })}
            </Grid>
          )}

          {openEditorError && <ModalWarningText warning={openEditorError} />}
          {!openEditorError && !isPosConflict && <ModalWarningText warning={`Warning: ${conflict}`} />}
          {!openEditorError && isPosConflict && posNoChange && <ModalWarningText warning={`Warning: ${conflict}`} />}
          {apiError && <ModalErrorText error={`Error: ${apiError}`} />}

          <DialogActions>
            {noPosConflict ? (
              <ModalButton label="Close" handler={handleCancel}></ModalButton>
            ) : (
              <>
                <ModalButton label="Cancel" handler={handleCancel} disable={isSubmitting}></ModalButton>
                <ModalButton
                  label="Assign"
                  disable={!!openEditorError || !!apiError || isSubmitting}
                  handler={assignPairingToCrew}
                ></ModalButton>
              </>
            )}
          </DialogActions>
        </div>
      </DialogContent>
    );
  } else {
    return null;
  }
};
